import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import apiClient from '../api/client.ts';
import type { SearchResponseData } from '../components/search/searchTypes.ts';

const EMPTY: SearchResponseData = { results: [], counts: {}, total: 0 };

// Debounced, abortable GET /search. Keeps the PREVIOUS response visible while a new request is
// in flight (`loading` toggles independently of `data`) so the dropdown doesn't flash empty on
// every keystroke -- callers show a subtle loading indicator instead.
export function useGlobalSearch(query: string, activeType: string | null) {
  const [data, setData] = useState<SearchResponseData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      abortRef.current?.abort();
      setData(EMPTY);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params: Record<string, string> = { q: trimmed };
      if (activeType) params.type = activeType;

      apiClient.get<{ success: boolean; data: SearchResponseData }>('/search', { params, signal: controller.signal })
        .then((res) => { setData(res.data?.data ?? EMPTY); })
        .catch((err) => { if (!axios.isCancel(err)) setData(EMPTY); })
        .finally(() => { if (abortRef.current === controller) setLoading(false); });
    }, 200);

    return () => clearTimeout(debounceRef.current);
  }, [query, activeType]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { ...data, loading };
}
